export const FLAG_SCOPES = {
  GLOBAL: 'global',
  HOMEPAGE: 'homepage',
  CATEGORY: 'category',
  PRODUCT: 'product',
  COMPARE: 'compare',
  ARTICLE: 'article',
  SEM_SERP: 'sem-serp',
  SEM_LISTING: 'sem-listing',
};

const matchesPathPattern = (pathname = '', pattern) => {
  if (!pattern) return false;

  if (pattern.endsWith('*')) {
    return pathname.startsWith(pattern.slice(0, -1));
  }

  return pathname === pattern;
};

const matchesPathList = (pathname, patterns = []) => {
  return patterns.some((pattern) => matchesPathPattern(pathname, pattern));
};

// Category (SERP) pages are served under public URLs like
// `/{cluster}-software/{categorySlug}/` (optionally followed by pagination,
// facet or filter segments). Middleware evaluates flag scopes against the
// public pathname, so the match must be against these public URLs (mirrors how
// the `article` scope matches `/resources/`).
//
// Excluded from the CATEGORY scope:
// - `/{cluster}-software/a/{slug}...` — product detail pages (own routes)
// - `/{cluster}-software/{slug}/category-leaders/` — own page
//   (`pages/categoryLeaders.js`), never renders `ListingCard`
// - `/{cluster}-software/{slug}/{product}-vs-{product}/` — comparison pages
//   (rewrite to `/compare/`), never render `ListingCard`
//
// This keeps GA4 experiment attribution clean: flags in the CATEGORY scope are
// only reported on paths where the category-SERP variants can actually render.
const NON_SERP_CATEGORY_SEGMENTS = new Set(['category-leaders']);

const isCategoryPath = (pathname = '') => {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length < 2) return false;
  if (!segments[0].endsWith('-software')) return false;
  if (segments[1] === 'a') return false; // product detail
  // Exclude non-SERP category subpages (category-leaders, product comparisons).
  return !segments
    .slice(2)
    .some(
      (segment) =>
        NON_SERP_CATEGORY_SEGMENTS.has(segment) || segment.includes('-vs-')
    );
};

// Product (listing-detail) pages are served under public URLs like
// `/{cluster}-software/a/{slug}/`. Middleware evaluates flag scopes against the
// public pathname set by `extraHeaders` before Next.js applies the rewrite to
// `/listingDetail`, so this must key off the public URL.
//
// Exactly three path segments are required so sub-routes like
// `/{cluster}-software/a/{slug}/alternatives/` (which rewrite to their own
// internal pages and never render the pricing banner) are excluded.
const isListingDetailPath = (pathname = '') => {
  const segments = pathname.split('/').filter(Boolean);
  return (
    segments.length === 3 &&
    segments[0].endsWith('-software') &&
    segments[1] === 'a'
  );
};

export const getRequestScopes = (pathname = '') => {
  const scopes = [FLAG_SCOPES.GLOBAL];

  if (pathname === '/') {
    scopes.push(FLAG_SCOPES.HOMEPAGE);
  }

  if (pathname === '/compare') {
    scopes.push(FLAG_SCOPES.COMPARE);
  }

  if (pathname.startsWith('/resources/') && pathname !== '/resources/') {
    scopes.push(FLAG_SCOPES.ARTICLE);
  }

  if (
    ['/browse', '/serp', '/sem/serp'].includes(pathname) ||
    isCategoryPath(pathname)
  ) {
    scopes.push(FLAG_SCOPES.CATEGORY);
  }

  // SEM public URLs (see routes.js): `/p/sem/a/:slug` (listing detail) and
  // `/p/sem/:categorySlug-software` (SERP). Middleware evaluates flag scopes
  // against the public pathname set by `extraHeaders` before Next.js applies
  // the rewrite to `/sem/listingDetail` / `/sem/serp`, so scope matching must
  // key off the public URL. Listing detail must be checked FIRST so `/p/sem/a/`
  // does not also match the broader `/p/sem/` SERP prefix.
  if (pathname.startsWith('/p/sem/a/')) {
    scopes.push(FLAG_SCOPES.SEM_LISTING);
  } else if (pathname.startsWith('/p/sem/')) {
    scopes.push(FLAG_SCOPES.SEM_SERP);
  }

  if (isListingDetailPath(pathname)) {
    scopes.push(FLAG_SCOPES.PRODUCT);
  }

  return scopes;
};

export const matchesFlagScopes = (
  flagScopes = [FLAG_SCOPES.GLOBAL],
  pathname
) => {
  if (flagScopes.includes(FLAG_SCOPES.GLOBAL)) {
    return true;
  }

  const requestScopes = getRequestScopes(pathname);
  return flagScopes.some((scope) => requestScopes.includes(scope));
};

export const matchesFlagDefinition = (definition = {}, pathname = '') => {
  const {
    scopes = [FLAG_SCOPES.GLOBAL],
    includePaths = [],
    excludePaths = [],
  } = definition;

  if (!matchesFlagScopes(scopes, pathname)) {
    return false;
  }

  if (includePaths.length > 0 && !matchesPathList(pathname, includePaths)) {
    return false;
  }

  if (excludePaths.length > 0 && matchesPathList(pathname, excludePaths)) {
    return false;
  }

  return true;
};
